// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { AIFieldDefinition } from "@/types/deep-research-reports";

interface AIQueryColumnModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (field: Partial<AIFieldDefinition>) => void;
  initialData?: Partial<AIFieldDefinition>;
  isEditing?: boolean;
}

type OutputShape =
  | "short_answer"
  | "long_answer"
  | "single_select"
  | "multi_select";

const OUTPUT_SHAPE_LABELS: Record<OutputShape, string> = {
  short_answer: "Short Answer",
  long_answer: "Long Answer",
  single_select: "Single Select",
  multi_select: "Multi-Select",
};

export function AIQueryColumnModal({
  open,
  onClose,
  onSave,
  initialData,
  isEditing = false,
}: AIQueryColumnModalProps) {
  const [name, setName] = useState(initialData?.display_name || "");
  const [description, setDescription] = useState(
    initialData?.description || "",
  );
  const [aiInstructions, setAiInstructions] = useState(
    initialData?.ai_instructions || "",
  );
  const [outputShape, setOutputShape] = useState<OutputShape>("short_answer");
  const [selectionOptions, setSelectionOptions] = useState<string[]>(
    initialData?.allowed_values || [],
  );
  const [newOption, setNewOption] = useState("");

  // Reset form when modal opens with new values
  useEffect(() => {
    if (open) {
      setName(initialData?.display_name || "");
      setDescription(initialData?.description || "");
      setAiInstructions(initialData?.ai_instructions || "");
      setSelectionOptions(initialData?.allowed_values || []);

      // Determine initial output shape based on initial data
      if (initialData?.content_type === "long_text") {
        setOutputShape("long_answer");
      } else if (initialData?.content_type === "pick_list") {
        setOutputShape(
          initialData?.output_shape === "string_list"
            ? "multi_select"
            : "single_select",
        );
      } else {
        setOutputShape("short_answer");
      }

      setNewOption("");
    }
  }, [open, initialData]);

  const handleSave = () => {
    if (name.trim() && aiInstructions.trim()) {
      // Map output shape to backend configuration
      let contentType: AIFieldDefinition["content_type"];
      let outputShapeValue: AIFieldDefinition["output_shape"];

      switch (outputShape) {
        case "long_answer":
          contentType = "long_text";
          outputShapeValue = "string";
          break;
        case "single_select":
          contentType = "pick_list";
          outputShapeValue = "string";
          break;
        case "multi_select":
          contentType = "pick_list";
          outputShapeValue = "string_list";
          break;
        default: // short_answer
          contentType = "short_text";
          outputShapeValue = "string";
      }

      // For select options, ensure we have valid options
      const allowedValues =
        outputShape === "single_select" || outputShape === "multi_select"
          ? selectionOptions
          : [];

      onSave({
        display_name: name.trim(),
        description: description.trim() || null,
        ai_instructions: aiInstructions.trim(),
        content_type: contentType,
        output_shape: outputShapeValue,
        allowed_values: allowedValues,
      });

      // Reset form
      setName("");
      setDescription("");
      setAiInstructions("");
      setOutputShape("short_answer");
      setSelectionOptions([]);
      setNewOption("");
      onClose();
    }
  };

  const handleCancel = () => {
    setName("");
    setDescription("");
    setAiInstructions("");
    setOutputShape("short_answer");
    setSelectionOptions([]);
    setNewOption("");
    onClose();
  };

  const handleAddOption = () => {
    if (newOption.trim() && !selectionOptions.includes(newOption.trim())) {
      setSelectionOptions([...selectionOptions, newOption.trim()]);
      setNewOption("");
    }
  };

  const handleRemoveOption = (option: string) => {
    setSelectionOptions(selectionOptions.filter((o) => o !== option));
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} AI Field</DialogTitle>
          <DialogDescription>
            Configure an AI-powered field that will analyze each record in your
            dataset.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Field Name */}
          <div className="space-y-2">
            <Label htmlFor="field-name">Field Name</Label>
            <Input
              id="field-name"
              data-testid="ai-field-name"
              placeholder="e.g., Business Driver"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This will be the column header in your dataset
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="field-description">Description (Optional)</Label>
            <Textarea
              id="field-description"
              data-testid="ai-field-description"
              placeholder="e.g., Identifies the primary business need driving this opportunity"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Help others understand what this field captures
            </p>
          </div>

          {/* AI Instructions */}
          <div className="space-y-2">
            <Label htmlFor="ai-instructions">AI Instructions</Label>
            <Textarea
              id="ai-instructions"
              data-testid="ai-field-instructions"
              placeholder="e.g., Analyze the opportunity data and identify the main business driver or need that motivated this purchase decision"
              value={aiInstructions}
              onChange={(e) => setAiInstructions(e.target.value)}
              rows={3}
              className="resize-y"
            />
            <p className="text-xs text-muted-foreground">
              Tell the AI what question to answer or what to analyze
            </p>
          </div>

          {/* Output Shape */}
          <div className="space-y-2">
            <Label htmlFor="output-shape">Output Type</Label>
            <Select
              value={outputShape}
              onValueChange={(value: OutputShape) => setOutputShape(value)}
            >
              <SelectTrigger
                id="output-shape"
                data-testid="ai-field-output-shape"
              >
                <SelectValue placeholder="Select output type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(OUTPUT_SHAPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {outputShape === "short_answer" &&
                "Brief text response (up to ~100 characters)"}
              {outputShape === "long_answer" &&
                "Detailed text response with full explanation"}
              {outputShape === "single_select" &&
                "One option from a predefined list"}
              {outputShape === "multi_select" &&
                "Multiple options from a predefined list"}
            </p>
          </div>

          {/* Selection Options for Single/Multi Select */}
          {(outputShape === "single_select" ||
            outputShape === "multi_select") && (
            <div className="space-y-2">
              <Label>Selection Options</Label>

              {/* Add new option */}
              <div className="flex gap-2">
                <Input
                  placeholder="Enter an option"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  data-testid="ai-field-new-option"
                />
                <Button
                  type="button"
                  onClick={handleAddOption}
                  disabled={!newOption.trim()}
                  data-testid="ai-field-add-option"
                >
                  Add
                </Button>
              </div>

              {/* Display existing options */}
              {selectionOptions.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/30">
                  {selectionOptions.map((option, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="px-3 py-1 flex items-center gap-1"
                    >
                      {option}
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(option)}
                        className="ml-1 hover:bg-muted-foreground/20 rounded-full p-0.5"
                        aria-label={`Remove ${option}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {selectionOptions.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  No options added yet. Add at least one option.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              !name.trim() ||
              !aiInstructions.trim() ||
              ((outputShape === "single_select" ||
                outputShape === "multi_select") &&
                selectionOptions.length === 0)
            }
            data-testid="ai-field-save-button"
          >
            {isEditing ? "Save Changes" : "Add Field"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
